import { forwardRef } from "react";

type CheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "onChange" | "checked" | "defaultChecked"
> & {
  /** Controlled */
  checked?: boolean;
  /** Uncontrolled */
  defaultChecked?: boolean;

  /** 체크 변경 이벤트(둘 다 지원) */
  onCheckedChange?: (checked: boolean) => void;

  /** 라벨(문자/노드) */
  label?: React.ReactNode;

  /** 에러 UI */
  error?: boolean;

  /** 박스 크기 커스텀(기본 20px) */
  boxClassName?: string;

  /** 라벨 스타일 커스텀 */
  labelClassName?: string;
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      name,
      value,
      checked,
      defaultChecked,
      onCheckedChange,
      label,
      error = false,
      disabled = false,
      required = false,
      className = "",
      boxClassName = "",
      labelClassName = "",
      ...rest
    },
    ref,
  ) => {
    const isControlled = typeof checked === "boolean";

    const boxBase = "flex items-center justify-center rounded-[4px] transition-colors";
    const sizeBase = "w-6 h-6"; // 기본 20px
    const borderNormal = "border border-[#C1C7CD]";
    const borderError = "border border-[#FF3B30]";
    const checkedStyle = "peer-checked:bg-[#0068FF] peer-checked:border-[#0068FF]";
    const iconToggle = "[&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100";

    return (
      <label
        htmlFor={id}
        className={[
          "inline-flex items-center gap-2 md:gap-3",
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
          className,
        ].join(" ")}
      >
        {/* 실제 input */}
        <input
          ref={ref}
          id={id}
          name={name}
          value={value}
          type="checkbox"
          disabled={disabled}
          required={required}
          className="peer sr-only"
          aria-invalid={error || undefined}
          // controlled / uncontrolled 모두 지원
          {...(isControlled ? { checked } : { defaultChecked })}
          onChange={(e) => onCheckedChange?.(e.target.checked)}
          {...rest}
        />

        {/* 커스텀 박스 */}
        <div
          className={[
            boxBase,
            sizeBase,
            error ? borderError : borderNormal,
            checkedStyle,
            iconToggle,
            boxClassName,
          ].join(" ")}
        >
          <svg
            className="w-5 h-5 stroke-white transition-opacity"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        {label ? (
          <span
            className={[
              "select-none font-normal text-[16px] text-[#121319] leading-[24px] flex",
              disabled ? "cursor-not-allowed" : "cursor-pointer",
              labelClassName,
            ].join(" ")}
          >
            {label}
          </span>
        ) : null}
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";
export default Checkbox;
